import * as React from 'react'
import { useToast as useNimbusToast } from '@nimbus-ds/components'

// App-level toast facade. The public API — toast({ title, description, variant }) and
// useToast() — is preserved so the ~108 existing call sites are untouched. Rendering
// is delegated to Nimbus's <Toast.Provider> queue via a bridge (useToastBridge), so
// toasts look native. Nimbus toasts are a single text line + a type; we fold
// title/description into one string and map variant → Nimbus type.

type ToastVariant = 'default' | 'destructive' | 'success'
type NimbusToastType = 'primary' | 'success' | 'danger' | 'progress'

export interface ToastInput {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: ToastVariant
  // Back-compat: any other legacy shadcn ToastProps fields are accepted and ignored.
  [key: string]: unknown
}

// Imperative Nimbus API, captured once the provider mounts (see useToastBridge).
let addToastRef: ((t: { id: string; text: string; type?: NimbusToastType }) => void) | null = null
let closeToastRef: ((id: string) => void) | null = null
// Toasts fired before the provider mounts are queued and flushed on registration.
const pending: Array<{ id: string; text: string; type?: NimbusToastType }> = []

let count = 0
const genId = () => {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

const nodeToString = (n: React.ReactNode): string =>
  typeof n === 'string' || typeof n === 'number' ? String(n) : ''
const toText = (title?: React.ReactNode, description?: React.ReactNode) =>
  [nodeToString(title), nodeToString(description)].filter(Boolean).join(' — ')

const variantToType = (v?: ToastVariant): NimbusToastType =>
  v === 'destructive' ? 'danger' : v === 'success' ? 'success' : 'primary'

function toast({ title, description, variant }: ToastInput) {
  const id = genId()
  const payload = { id, text: toText(title, description), type: variantToType(variant) }
  if (addToastRef) addToastRef(payload)
  else pending.push(payload)
  const dismiss = () => closeToastRef?.(id)
  return { id, dismiss, update: () => {} }
}

// Mount ONCE inside the Nimbus <Toast.Provider> (via <Toaster />). Bridges the
// module-level toast() facade onto Nimbus's imperative queue.
export function useToastBridge() {
  const { addToast, closeToast } = useNimbusToast()
  React.useEffect(() => {
    addToastRef = (t) => addToast(t)
    closeToastRef = closeToast
    if (pending.length) pending.splice(0).forEach((p) => addToast(p))
    return () => {
      addToastRef = null
      closeToastRef = null
    }
  }, [addToast, closeToast])
  return null
}

function useToast() {
  return {
    toast,
    dismiss: (id?: string) => {
      if (id) closeToastRef?.(id)
    },
  }
}

export { useToast, toast }
